import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authcontext';

function Login() {
  const navigate = useNavigate();
  const { login, user, loading, error: authError } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showCredentials, setShowCredentials] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);


  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      if (user.role === 'admin') {
        navigate('/admin/dashboard');
      } else if (user.role === 'guest') {
        navigate('/guest');
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const result = await login(username, password);

      if (result.success) {
        const role = result.user.role;
        if (role === 'admin') {
          navigate('/admin/dashboard');
        } else if (role === 'guest') {
          navigate('/guest');
        }
      } else {
        setError(result.message || 'Invalid username or password');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error('Login error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Responsive values based on screen size
  const isMobile = windowWidth <= 480;
  const isTablet = windowWidth > 480 && windowWidth <= 768;
  const isSmallLaptop = windowWidth > 768 && windowWidth <= 1024;
  const isDesktop = windowWidth > 1024;

  const styles = {
    container: {
      display: 'flex',
      height: isDesktop ? '100vh' : 'auto',
      minHeight: isMobile || isTablet || isSmallLaptop ? '100vh' : '100vh',
      position: 'relative',
      overflow: isDesktop ? 'hidden' : 'auto',
      fontFamily: "'Playfair Display', 'Georgia', serif",
      flexDirection: isMobile || isTablet || isSmallLaptop ? 'column' : 'row'
    },
    imageSection: {
      flex: isDesktop ? 1 : 'none',
      position: 'relative',
      height: isDesktop ? '100vh' : 'auto',
      overflow: 'hidden',
      display: isMobile || isTablet || isSmallLaptop ? 'none' : 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#1a1a1a'
    },
    hotelImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      opacity: imageLoaded ? 0.9 : 0,
      transition: 'opacity 1s ease-in-out',
      filter: 'brightness(0.7) contrast(1.1)'
    },
    imageOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(135deg, rgba(139, 69, 19, 0.3) 0%, rgba(0, 0, 0, 0.5) 100%)',
      pointerEvents: 'none'
    },
    brandOverlay: {
      position: 'absolute',
      bottom: isMobile ? '20px' : isTablet ? '30px' : '60px',
      left: isMobile ? '20px' : isTablet ? '30px' : '60px',
      color: 'white',
      zIndex: 2,
      opacity: imageLoaded ? 1 : 0,
      transform: imageLoaded ? 'translateY(0)' : 'translateY(20px)',
      transition: 'all 1s ease-out 0.3s'
    },
    brandTitle: {
      fontSize: isMobile ? '28px' : isTablet ? '36px' : '48px',
      fontWeight: '700',
      margin: 0,
      letterSpacing: isMobile ? '1px' : '2px',
      textShadow: '2px 2px 20px rgba(0,0,0,0.5)'
    },
    brandSubtitle: {
      fontSize: isMobile ? '12px' : isTablet ? '14px' : '18px',
      fontWeight: '300',
      margin: '8px 0 0 0',
      letterSpacing: isMobile ? '2px' : '4px',
      textTransform: 'uppercase',
      opacity: 0.9
    },
    formSection: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '30px 20px' : isTablet ? '40px 30px' : isSmallLaptop ? '40px 30px' : '40px 40px',
      background: 'linear-gradient(135deg, #f5f5f0 0%, #e8e5da 100%)',
      position: 'relative',
      height: isDesktop ? '100vh' : 'auto',
      minHeight: isMobile || isTablet || isSmallLaptop ? '100vh' : '100vh',
      overflow: isDesktop ? 'hidden' : 'auto',
      overflowX: 'hidden'
    },
    decorativePattern: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4cfc0' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      opacity: 0.4,
      pointerEvents: 'none'
    },
    formContainer: {
      width: '100%',
      maxWidth: isMobile ? '100%' : isTablet ? '420px' : isSmallLaptop ? '460px' : '500px',
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      borderRadius: isMobile ? '16px' : '20px',
      padding: isMobile ? '30px 20px' : isTablet ? '35px 30px' : isSmallLaptop ? '40px 35px' : '45px 40px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 0 1px rgba(0, 0, 0, 0.1)',
      position: 'relative',
      zIndex: 1,
      animation: 'slideUp 0.8s ease-out',
      border: '1px solid rgba(139, 69, 19, 0.1)',
      margin: isMobile || isTablet || isSmallLaptop ? '20px auto' : '0',
      maxHeight: isDesktop ? '90vh' : 'none',
      overflowY: isDesktop ? 'auto' : 'visible'
    },
    header: {
      textAlign: 'center',
      marginBottom: isMobile ? '18px' : isTablet ? '22px' : isSmallLaptop ? '24px' : '26px',
      position: 'relative'
    },
    logoIcon: {
      fontSize: isMobile ? '32px' : isTablet ? '38px' : isSmallLaptop ? '40px' : '42px',
      marginBottom: isMobile ? '8px' : isTablet ? '10px' : '12px',
      display: 'block'
    },
    title: {
      fontSize: isMobile ? '22px' : isTablet ? '26px' : isSmallLaptop ? '28px' : '30px',
      fontWeight: '700',
      color: '#2c2416',
      marginBottom: '6px',
      letterSpacing: isMobile ? '0.5px' : '1px'
    },
    subtitle: {
      fontSize: isMobile ? '11px' : isTablet ? '12px' : '14px',
      color: '#8b7355',
      margin: '0',
      fontWeight: '400',
      letterSpacing: isMobile ? '1px' : '2px',
      textTransform: 'uppercase',
      fontFamily: "'Montserrat', sans-serif"
    },
    divider: {
      height: '2px',
      width: isMobile ? '40px' : '60px',
      background: 'linear-gradient(to right, transparent, #c9a959, transparent)',
      margin: isMobile ? '10px auto' : isTablet ? '12px auto' : '12px auto',
      borderRadius: '2px'
    },
    form: {
      display: 'flex',
      flexDirection: 'column'
    },
    inputGroup: {
      marginBottom: isMobile ? '14px' : isTablet ? '16px' : '18px',
      position: 'relative'
    },
    label: {
      display: 'block',
      marginBottom: isMobile ? '6px' : '7px',
      fontSize: isMobile ? '10px' : '12px',
      fontWeight: '600',
      color: '#2c2416',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      fontFamily: "'Montserrat', sans-serif"
    },
    input: {
      width: '100%',
      padding: isMobile ? '12px 14px' : isTablet ? '13px 16px' : '14px 18px',
      fontSize: isMobile ? '14px' : '15px',
      border: '2px solid #e0d5c7',
      borderRadius: isMobile ? '10px' : '12px',
      outline: 'none',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box',
      background: '#fafaf8',
      color: '#2c2416',
      fontFamily: "'Montserrat', sans-serif",
      WebkitAppearance: 'none',
      appearance: 'none'
    },
    button: {
      width: '100%',
      padding: isMobile ? '13px' : isTablet ? '14px' : '15px',
      fontSize: isMobile ? '12px' : '14px',
      fontWeight: '700',
      color: 'white',
      background: 'linear-gradient(135deg, #c9a959 0%, #b8954a 100%)',
      border: 'none',
      borderRadius: isMobile ? '10px' : '12px',
      cursor: 'pointer',
      transition: 'all 0.4s ease',
      marginTop: '6px',
      letterSpacing: isMobile ? '1.5px' : '2px',
      textTransform: 'uppercase',
      fontFamily: "'Montserrat', sans-serif",
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 8px 20px rgba(201, 169, 89, 0.3)',
      opacity: isSubmitting ? 0.7 : 1,
      WebkitTapHighlightColor: 'transparent',
      touchAction: 'manipulation'
    },
    buttonShine: {
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
      transition: 'left 0.6s ease'
    },
    error: {
      background: 'linear-gradient(135deg, #fff5f5 0%, #ffe5e5 100%)',
      color: '#c53030',
      padding: isMobile ? '9px 11px' : '10px 14px',
      borderRadius: isMobile ? '10px' : '12px',
      marginBottom: isMobile ? '12px' : '14px',
      fontSize: isMobile ? '12px' : '13px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      border: '1px solid #feb2b2',
      fontFamily: "'Montserrat', sans-serif",
      fontWeight: '500'
    },
    credentialsBox: {
      marginTop: isMobile ? '16px' : isTablet ? '18px' : '20px',
      padding: isMobile ? '12px' : isTablet ? '14px' : '16px',
      background: 'linear-gradient(135deg, #f8f6f1 0%, #ebe8dc 100%)',
      borderRadius: isMobile ? '10px' : '12px',
      border: '1px solid #e8e5da',
      fontSize: isMobile ? '11px' : '12px'
    },
    credentialsTitle: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: showCredentials ? (isMobile ? '8px' : '12px') : '0',
      fontWeight: '600',
      color: '#2c2416',
      fontSize: isMobile ? '11px' : '12px',
      fontFamily: "'Montserrat', sans-serif"
    },
    toggleButton: {
      background: 'none',
      border: 'none',
      color: '#c9a959',
      cursor: 'pointer',
      fontSize: isMobile ? '10px' : '11px',
      fontWeight: '700',
      padding: isMobile ? '5px 8px' : '6px 10px',
      borderRadius: '6px',
      transition: 'all 0.3s',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      fontFamily: "'Montserrat', sans-serif",
      WebkitTapHighlightColor: 'transparent'
    },
    credentialItem: {
      padding: isMobile ? '9px' : isTablet ? '10px' : '12px',
      background: 'white',
      borderRadius: isMobile ? '8px' : '10px',
      marginBottom: isMobile ? '6px' : '8px',
      border: '1px solid #e8e5da',
      transition: 'all 0.3s ease',
      fontSize: isMobile ? '10px' : '12px',
      fontFamily: "'Montserrat', sans-serif",
      lineHeight: '1.6'
    },
    roleTag: {
      display: 'inline-block',
      padding: isMobile ? '2px 6px' : '3px 8px',
      borderRadius: '6px',
      fontSize: isMobile ? '8px' : '9px',
      fontWeight: '700',
      marginLeft: '6px',
      letterSpacing: '0.5px',
      fontFamily: "'Montserrat', sans-serif"
    },
    adminTag: {
      background: 'linear-gradient(135deg, #c9a959, #b8954a)',
      color: 'white'
    },
    guestTag: {
      background: 'linear-gradient(135deg, #4a90e2, #357abd)',
      color: 'white'
    },
    infoBox: {
      marginTop: isMobile ? '12px' : '14px',
      padding: isMobile ? '9px 11px' : '10px 14px',
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderRadius: isMobile ? '10px' : '12px',
      fontSize: isMobile ? '10px' : '12px',
      color: '#166534',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      border: '1px solid #bbf7d0',
      fontFamily: "'Montserrat', sans-serif",
      fontWeight: '500'
    },
    keyframes: `
      @keyframes slideUp {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      @keyframes pulse {
        0%, 100% {
          opacity: 1;
        }
        50% {
          opacity: 0.5;
        }
      }

      /* Smooth scrolling */
      * {
        -webkit-overflow-scrolling: touch;
      }

      /* Hide scrollbar but keep functionality on desktop form */
      @media (min-width: 1025px) {
        .form-container-scroll {
          scrollbar-width: thin;
          scrollbar-color: #c9a959 transparent;
        }
        
        .form-container-scroll::-webkit-scrollbar {
          width: 6px;
        }
        
        .form-container-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .form-container-scroll::-webkit-scrollbar-thumb {
          background-color: #c9a959;
          border-radius: 10px;
        }
      }

      /* Additional responsive utilities */
      @media (hover: hover) and (pointer: fine) {
        button:hover {
          transform: translateY(-2px) !important;
        }
      }

      /* Prevent zoom on input focus for iOS */
      @media screen and (max-width: 480px) {
        input, select, textarea {
          font-size: 16px !important;
        }
      }

      /* Ensure proper viewport height on mobile browsers */
      @media (max-width: 1024px) {
        html, body {
          height: auto;
          min-height: 100vh;
        }
      }

      /* Prevent body scroll on desktop */
      @media (min-width: 1025px) {
        body {
          overflow: hidden;
        }
      }
    `
  };

  if (loading) {
    return (
      <div style={{
        ...styles.container,
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column'
      }}>
        <style>{styles.keyframes}</style>
        <div style={{ textAlign: 'center', color: '#c9a959' }}>
          <div style={{
            fontSize: isMobile ? '36px' : '48px',
            marginBottom: '20px',
            animation: 'pulse 1.5s ease-in-out infinite'
          }}>
            🏨
          </div>
          <p style={{
            fontSize: isMobile ? '14px' : '18px',
            fontFamily: "'Montserrat', sans-serif",
            letterSpacing: '2px',
            color: '#2c2416'
          }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{styles.keyframes}</style>

      {/* Image Section - Only shown on desktop */}
      {isDesktop && (
        <div style={styles.imageSection}>
          <img
            src="https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=1200&h=1600&fit=crop&q=80"
            alt="Luxury Hotel"
            style={styles.hotelImage}
            onLoad={() => setImageLoaded(true)}
          />
          <div style={styles.imageOverlay}></div>
          <div style={styles.brandOverlay}>
            <h1 style={styles.brandTitle}>Hotel Management System</h1>
            <p style={styles.brandSubtitle}>Learning Hub</p>
          </div>
        </div>
      )}

      {/* Form Section */}
      <div style={styles.formSection}>
        <div style={styles.decorativePattern}></div>
        <div style={styles.formContainer} className="form-container-scroll">
          <div style={styles.header}>
            <span style={styles.logoIcon}>🏨</span>
            <h1 style={styles.title}>Welcome Back</h1>
            <div style={styles.divider}></div>
            <p style={styles.subtitle}>Management Portal</p>
          </div>

          <form onSubmit={handleSubmit} style={styles.form}>
            {error && (
              <div style={styles.error}>
                <span>⚠️</span>
                <span>{error}</span>
              </div>
            )}
            {authError && (
              <div style={styles.error}>
                <span>⚠️</span>
                <span>{authError}</span>
              </div>
            )}

            <div style={styles.inputGroup}>
              <label style={styles.label}>Username</label>
              <input
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                style={styles.input}
                onFocus={(e) => {
                  e.target.style.borderColor = '#c9a959';
                  e.target.style.background = '#fff';
                  e.target.style.boxShadow = '0 4px 12px rgba(201, 169, 89, 0.15)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e0d5c7';
                  e.target.style.background = '#fafaf8';
                  e.target.style.boxShadow = 'none';
                }}
                required
                disabled={isSubmitting}
                autoCapitalize="none"
                autoCorrect="off"
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Password</label>
              <input
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={styles.input}
                onFocus={(e) => {
                  e.target.style.borderColor = '#c9a959';
                  e.target.style.background = '#fff';
                  e.target.style.boxShadow = '0 4px 12px rgba(201, 169, 89, 0.15)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e0d5c7';
                  e.target.style.background = '#fafaf8';
                  e.target.style.boxShadow = 'none';
                }}
                required
                disabled={isSubmitting}
                autoCapitalize="none"
                autoCorrect="off"
              />
            </div>

            <button
              type="submit"
              style={styles.button}
              onMouseEnter={(e) => {
                if (!isSubmitting && !isMobile) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 12px 28px rgba(201, 169, 89, 0.4)';
                  const shine = e.target.querySelector('.button-shine');
                  if (shine) shine.style.left = '100%';
                }
              }}
              onMouseLeave={(e) => {
                if (!isMobile) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 8px 20px rgba(201, 169, 89, 0.3)';
                  const shine = e.target.querySelector('.button-shine');
                  if (shine) shine.style.left = '-100%';
                }
              }}
              disabled={isSubmitting}
            >
              <div style={styles.buttonShine} className="button-shine"></div>
              {isSubmitting ? 'Signing In...' : 'Sign In'}
            </button>
          </form>

          <div style={styles.credentialsBox}>
            {showCredentials && (
              <>
                <div
                  style={styles.credentialItem}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.target.style.transform = 'translateX(4px)';
                      e.target.style.borderColor = '#c9a959';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.target.style.transform = 'translateX(0)';
                      e.target.style.borderColor = '#e8e5da';
                    }
                  }}
                >
                  <strong>Admin Access</strong>
                  <span style={{ ...styles.roleTag, ...styles.adminTag }}>ADMIN</span>
                  <br />
                  <span style={{ fontSize: isMobile ? '9px' : '11px', color: '#8b7355' }}>
                    Username: <code style={{ background: '#f5f3ed', padding: '2px 6px', borderRadius: '4px', fontSize: isMobile ? '9px' : '10px' }}>admin</code> |
                    Password: <code style={{ background: '#f5f3ed', padding: '2px 6px', borderRadius: '4px', fontSize: isMobile ? '9px' : '10px' }}>admin123</code>
                  </span>
                </div>
                <div
                  style={styles.credentialItem}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.target.style.transform = 'translateX(4px)';
                      e.target.style.borderColor = '#c9a959';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.target.style.transform = 'translateX(0)';
                      e.target.style.borderColor = '#e8e5da';
                    }
                  }}
                >
                  <strong>Guest Access</strong>
                  <span style={{ ...styles.roleTag, ...styles.guestTag }}>GUEST</span>
                  <br />
                  <span style={{ fontSize: isMobile ? '9px' : '11px', color: '#8b7355' }}>
                    Username: <code style={{ background: '#f5f3ed', padding: '2px 6px', borderRadius: '4px', fontSize: isMobile ? '9px' : '10px' }}>guest</code> |
                    Password: <code style={{ background: '#f5f3ed', padding: '2px 6px', borderRadius: '4px', fontSize: isMobile ? '9px' : '10px' }}>guest123</code>
                  </span>
                </div>
              </>
            )}
          </div>

          <div style={styles.infoBox}>
            <span>✓</span>
            <span><strong>Database Connected:</strong> Real-time authentication enabled</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;